import React, { useEffect, useRef, useState } from "react";
import { SUPERADMIN_USER_ID } from "constants/constants";
import {
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { FaChartPie, FaCube } from "react-icons/fa";
import { FiCalendar } from "react-icons/fi";
import { TbLayoutSidebarLeftExpandFilled, TbLayoutSidebarRightExpandFilled, TbPlugConnected } from "react-icons/tb";
import { ModelInfo, ColumnInfo } from "build-ts/metadata";
import { ConfigurationContextProvider, useConfigurationContext } from "context/ConfigurationContext";
import { MdBarChart, MdBusiness, MdMemory, MdNumbers } from "react-icons/md";
import { PiTarget } from "react-icons/pi";
import { useUser } from "../../context/UserContext";
import { useUIContext } from "context/UIContext";
import { BiSolidNetworkChart } from "react-icons/bi";
import { useLocation, useNavigate, useMatch } from "react-router-dom";
import { GiBrain } from "react-icons/gi";
import ConfigPageMainPanel from "components/configuration/ConfigPageMainPanel";

// ---------------------------------------------------------------------------
// ConfigurationPage
// ---------------------------------------------------------------------------

const ENTITY_VIEWS = ["metrics", "benchmarks", "measures", "attributes", "time"];

const ONTOLOGY_NAV = [
  { label: "Graph", icon: BiSolidNetworkChart, key: "ViewMKGContainer", boxSize: 6 },
  { label: "Metrics", icon: MdBarChart, key: "metrics", boxSize: 6 },
  { label: "Measures", icon: MdNumbers, key: "measures", boxSize: 6 },
  { label: "Attributes", icon: FaChartPie, key: "attributes", boxSize: 4 },
  { label: "Datetime", icon: FiCalendar, key: "time", boxSize: 5 },
  { label: "Benchmarks", icon: PiTarget, key: "benchmarks", boxSize: 6 },
] as const;

/** Context sidebar: Memory is live; other entries are placeholders */
const CONTEXT_NAV = [
  { label: "Memory", icon: MdMemory, boxSize: 6, key: "memory" },
  { label: "Company Profile", icon: MdBusiness, boxSize: 5, key: "companyProfile" }
] as const;

const ConfigurationPage: React.FC = () => (
  <ConfigurationContextProvider>
    <ConfigurationPageContent />
  </ConfigurationContextProvider>
);

const ConfigurationPageContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch("/mkg/models/:id");
  const tsMatch = useMatch("/mkg/thoughtspot/:tsId");
  const metricsIdMatch = useMatch("/mkg/metrics/:id");
  const measuresIdMatch = useMatch("/mkg/measures/:id");
  const attributesIdMatch = useMatch("/mkg/attributes/:id");
  const timeframesIdMatch = useMatch("/mkg/timeframes/:id");
  const benchmarksIdMatch = useMatch("/mkg/benchmarks/:id");

  const { setIsNavbarCollapsed } = useUIContext();
  const { userId } = useUser();
  const { fetchModelList, fetchKgColumns } = useConfigurationContext();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [activeButton, setActiveButton] = useState<string>("ViewMKGContainer");
  const [modelInfo, setModelInfo] = useState<ModelInfo | undefined>(undefined);
  const [addedColumns, setAddedColumns] = useState<ColumnInfo[]>([]);
  const [removedColumns, setRemovedColumns] = useState<ColumnInfo[]>([]);
  const [shouldReloadKG, setShouldReloadKG] = useState<boolean>(false);

  // Keep a ref so the location effect can read activeButton without re-running
  const activeButtonRef = useRef(activeButton);
  useEffect(() => { activeButtonRef.current = activeButton; }, [activeButton]);

  // Fetch model list when the component mounts
  useEffect(() => {
    fetchModelList();
    // Fetch kg columns
    fetchKgColumns();
  }, []);

  // URL-driven navigation — preserve entity views when transitioning to /mkg
  useEffect(() => {
    if (location.pathname === "/mkg") {
      setIsNavbarCollapsed(true);
      if (!ENTITY_VIEWS.includes(activeButtonRef.current)) {
        setActiveButton("ViewMKGContainer");
      }
    } else if (location.pathname === "/mkg/metrics" || metricsIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("metrics");
    } else if (location.pathname === "/mkg/measures" || measuresIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("measures");
    } else if (location.pathname === "/mkg/attributes" || attributesIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("attributes");
    } else if (location.pathname === "/mkg/timeframes" || timeframesIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("time");
    } else if (location.pathname === "/mkg/benchmarks" || benchmarksIdMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("benchmarks");
    } else if (location.pathname === "/mkg/models") {
      setIsNavbarCollapsed(true);
      setActiveButton("ankoModel");
    } else if (location.pathname === "/mkg/models/new") {
      setIsNavbarCollapsed(true);
      setActiveButton("createModel");
    } else if (match) {
      setIsNavbarCollapsed(true);
      setActiveButton("updateModel");
    } else if (location.pathname === "/mkg/thoughtspot" || tsMatch) {
      setIsNavbarCollapsed(true);
      setActiveButton("thoughtspotEmbed");
    } else if (location.pathname === "/mkg/memory") {
      setIsNavbarCollapsed(true);
      setActiveButton("memory");
    }
  }, [location.pathname, match, tsMatch, metricsIdMatch, measuresIdMatch, attributesIdMatch, timeframesIdMatch, benchmarksIdMatch]);

  const handleReloadKnowledgeGraph = () => {
    setShouldReloadKG(true);
    setTimeout(() => setShouldReloadKG(false), 100);
  };

  const goToEntity = (key: string) => {
    setActiveButton(key);
    navigate(`/mkg/${key}`);
  };

  const goToMemory = () => {
    setActiveButton("memory");
    if (location.pathname !== "/mkg/memory") navigate("/mkg/memory");
    setIsSidebarCollapsed(true);
  };

  return (
    <Grid
      templateAreas={"\"sidebar main\""}
      gridTemplateRows="1fr"
      gridTemplateColumns={isSidebarCollapsed ? "60px 1fr" : "220px 1fr"}
      height="100%"
      p={0}
    >
      {/* ---------------------------------------------------------------- */}
      {/* Sidebar                                                           */}
      {/* ---------------------------------------------------------------- */}
      <GridItem area="sidebar" bg="gray.50" borderRightWidth="1px" borderColor="gray.200">
        {isSidebarCollapsed ? (
          /* Collapsed icon rail */
          <Flex direction="column" align="center" h="full" pt={3} pb={4} gap={1}>
            <Tooltip label="Expand sidebar" placement="right" hasArrow bg="gray.700" color="white" fontSize="xs">
              <IconButton
                aria-label="Expand sidebar"
                icon={<Icon as={TbLayoutSidebarLeftExpandFilled} boxSize={7} />}
                variant="ghost"
                size="sm"
                color="gray.400"
                _hover={{ bg: "gray.100", color: "gray.700" }}
                onClick={() => setIsSidebarCollapsed(false)}
              />
            </Tooltip>
            <Divider borderColor="gray.200" mb={2} />

            {ONTOLOGY_NAV.map(({ label, icon: NavIcon, key, boxSize }) => (
              <Tooltip key={key} label={label} placement="right" hasArrow bg="gray.700" color="white" fontSize="xs">
                <IconButton
                  aria-label={label}
                  icon={<Icon as={NavIcon} boxSize={boxSize} />}
                  variant="ghost"
                  size="sm"
                  color={activeButton === key ? "purple.700" : "gray.500"}
                  bg={activeButton === key ? "purple.50" : "transparent"}
                  _hover={{ bg: "gray.100", color: "gray.800" }}
                  onClick={() => key === "ViewMKGContainer" ? (setActiveButton("ViewMKGContainer"), navigate("/mkg")) : goToEntity(key)}
                />
              </Tooltip>
            ))}

            <Divider borderColor="gray.200" my={2} />
            {CONTEXT_NAV.map(({ label, icon: NavIcon, key, boxSize }) => (
              <Tooltip
                key={key}
                label={label === "Memory" ? label : `${label} (coming soon)`}
                placement="right"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="xs"
              >
                <IconButton
                  aria-label={label === "Memory" ? label : `${label} (coming soon)`}
                  icon={<Icon as={NavIcon} boxSize={boxSize} />}
                  variant="ghost"
                  size="sm"
                  color={activeButton === key ? "purple.700" : "gray.500"}
                  bg={activeButton === key ? "purple.50" : "transparent"}
                  _hover={{ bg: "gray.100", color: "gray.800" }}
                  onClick={() => key === "memory" ? goToMemory() : undefined}
                />
              </Tooltip>
            ))}
            <Divider borderColor="gray.200" my={2} />
            <Tooltip label="Models" placement="right" hasArrow bg="gray.700" color="white" fontSize="xs">
              <IconButton
                aria-label="Models"
                icon={<Icon as={FaCube} boxSize={5} />}
                variant="ghost"
                size="sm"
                color={activeButton === "ankoModel" ? "purple.700" : "gray.500"}
                bg={activeButton === "ankoModel" ? "purple.50" : "transparent"}
                _hover={{ bg: "gray.100", color: "gray.800" }}
                onClick={() => { setActiveButton("ankoModel"); navigate("/mkg/models"); }}
              />
            </Tooltip>
            {userId === SUPERADMIN_USER_ID && (
              <Tooltip label="Connections" placement="right" hasArrow bg="gray.700" color="white" fontSize="xs">
                <IconButton
                  aria-label="Connections"
                  icon={<Icon as={TbPlugConnected} boxSize={6} />}
                  variant="ghost"
                  size="sm"
                  color={activeButton === "thoughtspotEmbed" ? "purple.700" : "gray.500"}
                  bg={activeButton === "thoughtspotEmbed" ? "purple.50" : "transparent"}
                  _hover={{ bg: "gray.100", color: "gray.800" }}
                  onClick={() => { setActiveButton("thoughtspotEmbed"); navigate("/mkg/thoughtspot"); setIsSidebarCollapsed(false); }}
                />
              </Tooltip>
            )}

          </Flex>
        ) : (
          /* Expanded nav */
          <Flex direction="column" h="full">
            <VStack spacing={0} align="stretch" px={2} flex={1} overflowY="auto">
              <Flex align="center" justify="space-between" width="full" px={2} pt={2} gap={2}>
                <HStack spacing={2} flex={1} minW={0} align="center">
                  <Icon as={GiBrain} boxSize={6} color="purple.700" />
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color="purple.700"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={0}
                    textAlign="left"
                  >
                    Knowledge
                  </Text>
                </HStack>
                <Tooltip label="Collapse sidebar" placement="bottom" hasArrow bg="gray.700" color="white" fontSize="xs">
                  <IconButton
                    aria-label="Collapse sidebar"
                    icon={<Icon as={TbLayoutSidebarRightExpandFilled} boxSize={7} />}
                    variant="ghost"
                    size="sm"
                    color="gray.400"
                    flexShrink={0}
                    _hover={{ bg: "gray.100", color: "gray.600" }}
                    onClick={() => setIsSidebarCollapsed(true)}
                  />
                </Tooltip>
              </Flex>

              <Flex align="center" width="full" px={2} mt={6}>
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={0}
                  textAlign="left"
                  lineHeight="1.2"
                >
                  Sources
                </Text>
              </Flex>

              <Divider borderColor="gray.300" my={2} />

              <Flex
                as="button"
                align="center"
                width="full"
                px={2}
                py={2}
                borderRadius="md"
                cursor="pointer"
                color={activeButton === "ankoModel" ? "purple.700" : "gray.700"}
                bg={activeButton === "ankoModel" ? "purple.50" : "transparent"}
                _hover={{ bg: "gray.100", color: "gray.800" }}
                onClick={() => { setActiveButton("ankoModel"); navigate("/mkg/models"); }}
              >
                <Icon as={FaCube} boxSize={5} mr={3} minW={6} color={activeButton === "ankoModel" ? "purple.600" : "gray.500"} />
                <Text fontSize="sm" mb={0} fontWeight={activeButton === "ankoModel" ? "700" : "600"} color="inherit">
                  Data Models
                </Text>
              </Flex>
              {userId === SUPERADMIN_USER_ID && (
                <Flex
                  as="button"
                  align="center"
                  width="full"
                  px={2}
                  py={2}
                  borderRadius="md"
                  cursor="pointer"
                  color={activeButton === "thoughtspotEmbed" ? "purple.700" : "gray.700"}
                  bg={activeButton === "thoughtspotEmbed" ? "purple.50" : "transparent"}
                  _hover={{ bg: "gray.100", color: "gray.800" }}
                  onClick={() => { setActiveButton("thoughtspotEmbed"); navigate("/mkg/thoughtspot"); setIsSidebarCollapsed(true); }}
                >
                  <Icon as={TbPlugConnected} boxSize={6} mr={3} minW={6} color={activeButton === "thoughtspotEmbed" ? "purple.600" : "gray.500"} />
                  <Text fontSize="sm" mb={0} fontWeight={activeButton === "thoughtspotEmbed" ? "700" : "600"} color="inherit">
                    Connections
                  </Text>
                </Flex>
              )}

              <Flex align="center" width="full" px={2} mt={6}>
                <HStack spacing={0} flex={1} minW={0} align="center">
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={0}
                    textAlign="left"
                    lineHeight="1.2"
                  >
                    Ontology
                  </Text>
                </HStack>
              </Flex>
              <Divider borderColor="gray.300" my={2} />
              {ONTOLOGY_NAV.map(({ label, icon: NavIcon, key, boxSize }) => (
                <Flex
                  key={key}
                  as="button"
                  align="center"
                  width="full"
                  px={2}
                  py={2}
                  borderRadius="md"
                  cursor="pointer"
                  color={activeButton === key ? "purple.700" : "gray.700"}
                  bg={activeButton === key ? "purple.50" : "transparent"}
                  _hover={{ bg: "gray.100", color: "gray.800" }}
                  onClick={() => key === "ViewMKGContainer" ? (setActiveButton("ViewMKGContainer"), navigate("/mkg")) : goToEntity(key)}
                >
                  <Icon as={NavIcon} boxSize={boxSize} mr={3} minW={6} color={activeButton === key ? "purple.600" : "gray.500"} />
                  <Text fontSize="sm" mb={0} fontWeight={activeButton === key ? "700" : "600"} color="inherit" textAlign="left">
                    {label}
                  </Text>
                </Flex>
              ))}

              <Flex align="center" width="full" px={2} mt={6}>
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mb={0}
                  textAlign="left"
                  lineHeight="1.2"
                >
                  Context
                </Text>
              </Flex>
              <Divider borderColor="gray.300" my={2} />
              {CONTEXT_NAV.map(({ label, icon: NavIcon, key, boxSize }) => (
                <Flex
                  key={key}
                  as="button"
                  align="center"
                  width="full"
                  px={2}
                  py={2}
                  borderRadius="md"
                  cursor="pointer"
                  color={activeButton === key ? "purple.700" : "gray.700"}
                  bg={activeButton === key ? "purple.50" : "transparent"}
                  _hover={{ bg: "gray.100", color: "gray.800" }}
                  onClick={() => key === "memory" ? goToMemory() : undefined}
                >
                  <Icon as={NavIcon} boxSize={boxSize} mr={3} minW={6} color={activeButton === key ? "purple.600" : "gray.500"} />
                  <Text fontSize="sm" mb={0} fontWeight={activeButton === key ? "700" : "600"} color="inherit" textAlign="left">
                    {label}
                  </Text>
                </Flex>
              ))}
            </VStack>
          </Flex>
        )}
      </GridItem>

      {/* ---------------------------------------------------------------- */}
      {/* Main panel                                                        */}
      {/* ---------------------------------------------------------------- */}
      <GridItem area="main" height="100%" minH={0} overflow="hidden">
        <ConfigPageMainPanel
          activeButton={activeButton}
          shouldReloadKG={shouldReloadKG}
          modelInfo={modelInfo}
          setModelInfo={setModelInfo}
          addedColumns={addedColumns}
          removedColumns={removedColumns}
          setAddedColumns={setAddedColumns}
          setRemovedColumns={setRemovedColumns}
          fetchModelList={fetchModelList}
          handleReloadKnowledgeGraph={handleReloadKnowledgeGraph}
          tsMatchTsId={tsMatch?.params?.tsId}
        />
      </GridItem>
    </Grid>
  );
};

export default ConfigurationPage;

